import { useSearchParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { useTranslation } from "react-i18next";
import { invitationApi } from "@surgia/api/src/hub/invitation";

const REDIRECT_SECONDS = 3;

const VerifyInvitationPage = () => {
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<
    "success" | "error" | null
  >(null);
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const auth = useAuth();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation();

  const token = searchParams.get("invitationToken");

  useEffect(() => {
    const verifyInvitation = async () => {
      if (!token) {
        setVerificationStatus("error");
        setIsVerifying(false);
        return;
      }
      try {
        const response = await invitationApi.validateInvitation({
          token: token,
          component_id: import.meta.env.VITE_PUBLIC_COMPONENT_ID,
        });
        if (response.success && response.data?.isValid) {
          setVerificationStatus("success");
          sessionStorage.setItem("invitation_token", token);
        } else {
          setVerificationStatus("error");
        }
      } catch (error) {
        setVerificationStatus("error");
      } finally {
        setIsVerifying(false);
      }
    };
    verifyInvitation();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [token]);

  // Countdown and auto-redirect
  useEffect(() => {
    if (verificationStatus === "success") {
      timerRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current!);
            handleSignIn();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line
  }, [verificationStatus]);

  const handleSignIn = () => {
    if (token) {
      sessionStorage.setItem("invitation_token", token);
    }
    auth.signinRedirect();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="bg-white rounded-2xl shadow-lg py-8 px-6 w-full max-w-md border border-gray-200">
        <h2 className="text-xl mb-6">
          {verificationStatus === "error"
            ? t("hub.verifyInvitation.invitationError")
            : t("hub.verifyInvitation.invitationVerified")}
        </h2>
        <div className="mb-6">
          {isVerifying ? (
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mx-auto mb-4"></div>
              <p className="text-gray-600">
                {t("hub.verifyInvitation.verifying")}
              </p>
            </div>
          ) : verificationStatus === "success" ? (
            <>
              <div className="border border-gray-200 rounded-lg p-4 mb-6 text-left">
                <div className="text-md mb-1 text-black">
                  {t("hub.verifyInvitation.successTitle")}
                </div>
                <div className="text-gray-600 text-sm font-light">
                  {t("hub.verifyInvitation.successMessage", {
                    count: countdown,
                    plural: countdown !== 1 ? "s" : "",
                  })}
                </div>
              </div>
              <div className="w-full h-2 bg-purple-100 rounded-full mb-6 overflow-hidden">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${((REDIRECT_SECONDS - countdown) / REDIRECT_SECONDS) * 100}%`,
                    backgroundColor: "lch(41.32% 105.2 307.27)",
                  }}
                />
              </div>
              <button
                onClick={handleSignIn}
                style={{ backgroundColor: "lch(41.32% 105.2 307.27)" }}
                className="w-full py-2 rounded-lg text-white font-light hover:bg-purple-700 transition cursor-pointer"
              >
                {t("hub.verifyInvitation.signInNow")}
              </button>
            </>
          ) : (
            <div>
              <div className="border border-gray-200 rounded-lg px-4 py-3 mb-6 text-left">
                <div className="text-md text-red-600">
                  {t("hub.verifyInvitation.verificationFailed")}
                </div>
                <div className="text-red-600 text-sm font-light">
                  {t("hub.verifyInvitation.verificationFailedMessage")}
                </div>
              </div>
              <button
                onClick={() => (window.location.href = "/")}
                style={{ backgroundColor: "lch(41.32% 105.2 307.27)" }}
                className="w-full py-2 rounded-lg text-white font-light hover:bg-purple-700 transition cursor-pointer"
              >
                {t("hub.verifyInvitation.returnHome")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyInvitationPage;
